import { ISSUE_REASONS, type PreferenceIssue } from './types';

/**
 * Turns untrusted wire values into known codes, or into nothing.
 *
 * Nothing in here throws. The backend can legitimately ship a code before the frontend understands
 * it, and a preference layer that throws on an unfamiliar string takes down every page that renders
 * a number. An unusable value is dropped, reported, and replaced further up by `resolve`.
 *
 * Absence is not a problem and produces no report: most fields are optional on the wire, and a
 * profile that has simply not set one is the normal case rather than a fault.
 */

/** A problem awaiting the fallback that `resolve` will choose for it. */
export type CodeProblem = Omit<PreferenceIssue, 'resolvedTo'>;

export type Reading<T extends string> = {
  readonly value?: T;
  readonly problem?: CodeProblem;
};

const MAX_DESCRIBED_LENGTH = 60;

/** Renders any wire value for a log line without letting a huge one flood it. */
export const describeValue = (raw: unknown): string => {
  let rendered: string;

  if (typeof raw === 'string') {
    rendered = raw;
  } else {
    try {
      rendered = JSON.stringify(raw) ?? String(raw);
    } catch {
      rendered = Object.prototype.toString.call(raw);
    }
  }

  return rendered.length > MAX_DESCRIBED_LENGTH
    ? `${rendered.slice(0, MAX_DESCRIBED_LENGTH)}…`
    : rendered;
};

/**
 * Reads one code against its closed union.
 *
 * An exact match is tried first, then a folded form (trim, uppercase, hyphen to underscore). The
 * lenient pass exists because the codes have already been written down two different ways: the
 * mini-SEDD specifies `gal-us` where the service emits `GAL_US`. If that convention ever shifts,
 * strict matching would reject every field at once and silently drop every user to locale defaults
 * — a total outage of preferences, visible only in a log. Accepting the value and reporting it
 * keeps users working while still surfacing the drift. No two codes in any union differ only by
 * case or hyphen-vs-underscore, so folding cannot make a value ambiguous.
 */
export const foldCode = (value: string): string =>
  value.trim().toUpperCase().replace(/-/g, '_');

export const readCode = <T extends string>(
  allowed: readonly T[],
  raw: unknown,
  field: string
): Reading<T> => {
  if (raw === undefined || raw === null) return {};

  if (typeof raw !== 'string') {
    return {
      problem: {
        field,
        received: describeValue(raw),
        reason: ISSUE_REASONS.MALFORMED_VALUE,
      },
    };
  }

  if (allowed.includes(raw as T)) return { value: raw as T };

  const normalised = foldCode(raw);
  const match = allowed.find((code) => foldCode(code) === normalised);

  if (match) {
    return {
      value: match,
      problem: {
        field,
        received: describeValue(raw),
        reason: ISSUE_REASONS.MALFORMED_VALUE,
      },
    };
  }

  return {
    problem: {
      field,
      received: describeValue(raw),
      reason: ISSUE_REASONS.UNRECOGNISED_CODE,
    },
  };
};

/** A free-text wire string — locale — where any non-empty value is worth attempting. */
export const readString = (raw: unknown, field: string): Reading<string> => {
  if (raw === undefined || raw === null) return {};

  if (typeof raw !== 'string') {
    return {
      problem: {
        field,
        received: describeValue(raw),
        reason: ISSUE_REASONS.MALFORMED_VALUE,
      },
    };
  }

  const trimmed = raw.trim();
  if (!trimmed) return {};

  return { value: trimmed };
};

/**
 * A time zone, checked against the platform's own database.
 *
 * Worth validating here rather than downstream: an unknown IANA name throws inside
 * `Intl.DateTimeFormat`, so letting one through would move a `RangeError` from this file into
 * whichever cell happened to render a date first.
 */
export const readTimeZone = (raw: unknown, field: string): Reading<string> => {
  const { value, problem } = readString(raw, field);
  if (!value) return { problem };

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return { value, problem };
  } catch {
    return {
      problem: {
        field,
        received: describeValue(value),
        reason: ISSUE_REASONS.UNRECOGNISED_CODE,
      },
    };
  }
};
